import { APP_NAME, shareMatchInviteText } from "../../config/brand";
import { normalizeMatchRoomCode } from "../../utils/matchRoomCode";
import { openExternalTab } from "../../utils/openExternalTab";
import {
  inviteChannelFromUrl,
  trackInviteLinkCreated,
} from "../analytics/bridges/inviteAnalytics";

function reportInviteLinkSuccess(url) {
  if (!url || String(url).includes("/tv/")) return;
  trackInviteLinkCreated(inviteChannelFromUrl(url));
}

const APP_BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

/** Query flag on audience share links (`?room=&spectator=1`). */
export const SPECTATOR_SHARE_QUERY = "spectator";

/** Query flag on roster invite links (`?room=&spectator=1&roster=1`). */
export const ROSTER_JOIN_QUERY = "roster";

/**
 * @param {Pick<Location, "search">} location
 */
export function parseRosterJoinFromLocation(location) {
  if (!location?.search) return false;
  const params = new URLSearchParams(location.search);
  const v = (params.get(ROSTER_JOIN_QUERY) || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

/**
 * Spectator join URL that triggers auto-join after sign-in (`roster=1`).
 * @param {string} roomCode
 * @param {string} [origin]
 */
export function buildRosterJoinUrl(roomCode, origin) {
  const base = buildShareableMatchUrl(roomCode, origin);
  if (!base) return "";
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${ROSTER_JOIN_QUERY}=1`;
}

/**
 * @param {Pick<Location, "search">} location
 */
export function parseSpectatorShareFromLocation(location) {
  if (!location?.search) return false;
  const params = new URLSearchParams(location.search);
  const v = (params.get(SPECTATOR_SHARE_QUERY) || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

function parseMatchRoomFromPathname(pathname) {
  const marker = "/match/";
  const idx = (pathname || "").indexOf(marker);
  if (idx === -1) return "";
  const segment = pathname.slice(idx + marker.length).split("/")[0];
  if (!segment) return "";
  try {
    return normalizeMatchRoomCode(decodeURIComponent(segment));
  } catch {
    return normalizeMatchRoomCode(segment);
  }
}

/**
 * Parse a room code from the current location (path `/match/:code` or `?room=`).
 * Accepts full `window.location` paths (with `PUBLIC_URL` basename) or React Router paths.
 * @param {Pick<Location, "pathname" | "search">} location
 * @returns {string} normalized code or ""
 */
export function parseMatchRoomFromLocation(location) {
  if (!location) return "";
  const params = new URLSearchParams(location.search || "");
  const fromQuery = normalizeMatchRoomCode(params.get("room") || "");
  if (fromQuery) return fromQuery;
  return parseMatchRoomFromPathname(location.pathname || "");
}

/**
 * Build an absolute share URL for a match room.
 * Uses `?room=` so GitHub Pages loads index.html without relying on custom 404.html.
 * @param {string} roomCode
 * @param {string} [origin] defaults to `window.location.origin`
 */
export function buildShareableMatchUrl(roomCode, origin) {
  const code = normalizeMatchRoomCode(roomCode);
  if (!code) return "";
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const base = APP_BASE_PATH || "";
  const path = `${base}/?room=${encodeURIComponent(code)}&${SPECTATOR_SHARE_QUERY}=1`;
  return `${baseOrigin}${path}`;
}

/**
 * Build absolute URL for read-only TV / hall display (path route — scan-friendly).
 * Legacy `/?tv=` still handled by {@link AppHomeEntry}.
 * @param {string} roomCode
 * @param {string} [origin]
 */
export function buildTvModeUrl(roomCode, origin) {
  const code = normalizeMatchRoomCode(roomCode);
  if (!code) return "";
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const base = APP_BASE_PATH || "";
  const path = `${base}/tv/${encodeURIComponent(code)}`;
  return `${baseOrigin}${path}`;
}

/**
 * Same display with in-app back affordance (opened from match scoreboard).
 * @param {string} roomCode
 * @param {string} [origin]
 */
export function buildTvModeInAppUrl(roomCode, origin) {
  const url = buildTvModeUrl(roomCode, origin);
  if (!url) return "";
  return `${url}?from=match`;
}

/**
 * In-app router path (respects `PUBLIC_URL` basename).
 * @param {string} roomCode
 * @param {{ fromMatch?: boolean }} [opts]
 */
export function buildTvModePath(roomCode, opts = {}) {
  const code = normalizeMatchRoomCode(roomCode);
  if (!code) return "";
  const base = APP_BASE_PATH || "";
  const q = opts.fromMatch ? "?from=match" : "";
  return `${base}/tv/${encodeURIComponent(code)}${q}`;
}

/**
 * Arena TV — multi-court live board (Phase 3.0; foundation for Spectator Platform).
 * @param {string} roomCode
 * @param {{ fromCourt?: boolean }} [opts]
 */
export function buildArenaTvPath(roomCode, opts = {}) {
  const code = normalizeMatchRoomCode(roomCode);
  if (!code) return "";
  const base = APP_BASE_PATH || "";
  const q = opts.fromCourt ? "?from=court" : "";
  return `${base}/tv/${encodeURIComponent(code)}/arena${q}`;
}

/**
 * Absolute Arena TV URL for QR / share.
 * @param {string} roomCode
 * @param {string} [origin]
 */
export function buildArenaTvUrl(roomCode, origin) {
  const code = normalizeMatchRoomCode(roomCode);
  if (!code) return "";
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const base = APP_BASE_PATH || "";
  return `${baseOrigin}${base}/tv/${encodeURIComponent(code)}/arena`;
}

/**
 * Clipboard write with textarea fallback (works after async awaits / non-secure contexts).
 * @param {string} text
 * @returns {Promise<boolean>}
 */
async function writeTextToClipboard(text) {
  const value = String(text || "");
  if (!value) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through */
  }
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}

/**
 * Share via Web Share API or clipboard.
 * @returns {"shared" | "copied" | "cancelled" | "failed"}
 */
export async function shareMatchUrl({ url, title, text, copyText }) {
  if (!url) return "failed";

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    const classicText = shareMatchInviteText();
    const requested = (text || classicText).replace(/\s*\n+\s*/g, " · ").trim();
    const attempts = [{ title: title || APP_NAME, text: requested, url }];
    if (requested !== classicText || (title && title !== `${APP_NAME} match`)) {
      attempts.push({ title: `${APP_NAME} match`, text: classicText, url });
    }
    for (const data of attempts) {
      try {
        await navigator.share(data);
        reportInviteLinkSuccess(url);
        return "shared";
      } catch (e) {
        if (e?.name === "AbortError") return "cancelled";
      }
    }
  }

  const write = copyText || url;
  if (await writeTextToClipboard(write)) {
    reportInviteLinkSuccess(url);
    return "copied";
  }
  return "failed";
}

/**
 * Copy match URL to clipboard only (no Web Share sheet).
 * @returns {"copied" | "failed"}
 */
export async function copyMatchUrlToClipboard(url) {
  if (!url) return "failed";
  if (await writeTextToClipboard(url)) {
    reportInviteLinkSuccess(url);
    return "copied";
  }
  return "failed";
}

/**
 * @param {{ url: string; text?: string }} payload
 */
export function buildMatchShareBody({ url, text }) {
  const line = (text || shareMatchInviteText()).trim();
  if (!url) return line;
  return line ? `${line}\n${url}` : url;
}

/**
 * @param {{ url: string; text?: string }} payload
 */
export function buildWhatsAppShareUrl(payload) {
  const body = buildMatchShareBody(payload);
  return `https://api.whatsapp.com/send?text=${encodeURIComponent(body)}`;
}

/**
 * @param {{ url: string; text?: string }} payload
 * @returns {boolean} whether a tab/window was opened
 */
export function openWhatsAppShare(payload) {
  const href = buildWhatsAppShareUrl(payload);
  if (!href) return false;
  const ok = openExternalTab(href);
  if (ok) reportInviteLinkSuccess(payload.url);
  return ok;
}

/**
 * Instagram has no reliable web deep link for arbitrary URLs — copy message + open DMs.
 * @returns {"opened" | "copied" | "failed"}
 */
export async function shareMatchViaInstagram(payload) {
  const body = buildMatchShareBody(payload);
  if (!body) return "failed";

  let copied = false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(body);
      reportInviteLinkSuccess(payload.url);
      copied = true;
    }
  } catch {
    copied = false;
  }
  if (!copied) return "failed";

  const opened = openExternalTab("https://www.instagram.com/direct/inbox/");
  return opened ? "opened" : "copied";
}
